import React from 'react';
import { useFormContext } from 'react-hook-form';

import { AdminApprovalType } from '@column/api';
import { Button, Switch } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { StatusBadge } from '~/components/Table/StatusBadge';
import { AdminApprovalReviewDialog, useAdminApprovalReview, usePendingAdminApprovalsQuery } from '~/lib/approvals';
import { useSessionStore } from '~/stores/Session';

interface Props {
  value: boolean;
  onChange: (value: boolean) => void;
}

export const AdminApprovalConfiguration: React.FC<Props> = ({ value, onChange }) => {
  const { currentPlatform, isAuthorized, loadPlatform } = useSessionStore();
  const {
    formState: { isSubmitting },
  } = useFormContext();
  const canConfigure = isAuthorized({ permission: 'platformInfo', permissionLevel: 'write' });
  const { approvals, isLoading, isError, refetch } = usePendingAdminApprovalsQuery(
    AdminApprovalType.PLATFORM_UPDATE_REQUIRE_USER_CHANGE_APPROVAL,
    canConfigure
  );
  const { openReview, startReview, dialogProps } = useAdminApprovalReview(approvals);
  const pendingApproval = approvals[0];
  const pendingValue =
    pendingApproval?.request.platform_update_require_user_change_approval?.require_user_change_approval;

  return (
    <>
      <AdminApprovalReviewDialog {...dialogProps} hideCounter onReviewed={() => loadPlatform({})} />
      <PageActions>
        {startReview && (
          <Button type="button" size="sm" variant="secondary" onClick={startReview}>
            Review Changes
          </Button>
        )}
        <Button type="submit" form="platform-settings-form" size="sm" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Save Changes'}
        </Button>
      </PageActions>
      {/* Centered on the field label, which sits 12px down beside the control column. */}
      <div className="flex min-h-6 flex-wrap items-center gap-2 @tablet/form-field:mt-[9px]">
        {pendingApproval ? (
          <>
            <StatusBadge tone="warning">Pending approval</StatusBadge>
            <Button type="button" size="xs" variant="secondary" onClick={() => openReview(pendingApproval)}>
              {pendingApproval.can_review ? 'Review' : 'View'}
            </Button>
          </>
        ) : (
          <Switch
            id="user-change-approval"
            aria-label="User Access Changes"
            checked={value}
            disabled={!currentPlatform || !canConfigure || isLoading || isError || isSubmitting}
            onCheckedChange={onChange}
          />
        )}
      </div>
      {pendingApproval && (
        <p className="m-0 mt-1 pl-0.5 text-xs leading-[18px] text-muted-foreground">
          This setting will remain {pendingValue ? 'disabled' : 'enabled'} until an authorized user approves this
          change.
        </p>
      )}
      {isError && (
        <div role="alert" className="mt-3 flex items-center gap-3 text-sm text-foreground">
          Unable to load pending setting changes.
          <Button type="button" size="xs" variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      )}
    </>
  );
};
